import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [formData, setFormData] = useState({
    location: "",
    area_name: "",
    society_name: "",
    bhk: "",
    area_sqft: "",
    floor: "",
    age_years: "",
    has_roof_rights: false,
    furnishing_status: "Not Specified",
    furnishing_age: "0-2 years",
    room_sizes: "",
    position: ""
  });
  const [loading, setLoading] = useState(false);

  // Check URL parameter to auto-show form
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('start') === 'true') {
      setShowForm(true);
      setCurrentStep(0); // Reset to first step
    }
  }, [location.search, location.pathname]);

  const locations = [
    "Ghaziabad", 
    "Indirapuram", 
    "Vaishali", 
    "Vasundhara", 
    "Noida",
    "Shalimar Garden",
    "Kaushambi",
    "Crossings Republik",
    "Raj Nagar Extension",
    "Gurugram",
    "Noida Extension"
  ];
  const bhkOptions = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK"];
  const furnishingOptions = [
    { value: "new", label: "Newly Furnished (0-2 years)" },
    { value: "4-5years", label: "4-5 Years Old" },
    { value: "5+years", label: "More than 5 Years Old" }
  ];

  const steps = [
    {
      question: "Where is your property located?",
      subtitle: "Select your city or area",
      field: "location",
      type: "select",
      options: locations
    },
    {
      question: "What is the area name?",
      subtitle: "Enter the sector, khand, or locality name",
      field: "area_name",
      type: "text",
      placeholder: "e.g., Niti Khand 1, Vaibhav Khand, Sector 62"
    },
    {
      question: "What's the name of your society?",
      subtitle: "Enter the society name, or if it is a builder floor/flat, write 'Builder Flat'",
      field: "society_name",
      type: "text",
      placeholder: "e.g., ATS Society, Jaipuria Sunrise / Builder Floor"
    },
    {
      question: "What's the configuration?",
      subtitle: "Select your BHK type",
      field: "bhk",
      type: "buttons",
      options: bhkOptions
    },
    {
      question: "What's the total area?",
      subtitle: "Enter area in square feet",
      field: "area_sqft",
      type: "number",
      placeholder: "e.g., 1450"
    },
    {
      question: "Which floor is it on?",
      subtitle: "Enter the floor number",
      field: "floor",
      type: "text",
      placeholder: "e.g., 4th Floor"
    },
    {
      question: "How old is the property?",
      subtitle: "Age in years",
      field: "age_years",
      type: "number",
      placeholder: "e.g., 5"
    },
    {
      question: "How old is the furnishing/interior?",
      subtitle: "This affects the property valuation",
      field: "furnishing_age",
      type: "buttons",
      options: [
        { value: "0-2 years", label: "0-2 Years (Newly Furnished)" },
        { value: "3-5 years", label: "3-5 Years" },
        { value: "5+ years", label: "5+ Years (Old)" }
      ]
    },
    {
      question: "Is it front-facing or back-facing?",
      subtitle: "Select the position",
      field: "position",
      type: "buttons",
      options: [
        { value: "front", label: "Front-Facing" },
        { value: "back", label: "Back-Facing" }
      ]
    }
  ];

  const handleNext = () => {
    const currentField = steps[currentStep].field;
    if (!formData[currentField] && currentField !== "room_sizes") {
      alert("Please fill this field before continuing");
      return;
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (directFormData = null) => {
    setLoading(true);

    try {
      // Use directFormData if provided (for immediate submission), otherwise use state
      const dataToSubmit = directFormData || formData;
      
      const payload = {
        ...dataToSubmit,
        area_sqft: parseFloat(dataToSubmit.area_sqft),
        age_years: parseInt(dataToSubmit.age_years)
      };

      const response = await axios.post(`${API}/valuation`, payload);
      
      // Store valuation temporarily and show lead capture modal
      // Add timestamp to force component remount and prevent stale state
      navigate("/valuation-result", { 
        state: { 
          valuation: response.data,
          requiresLeadCapture: true,  // Flag to show lead form first
          timestamp: Date.now()  // Force fresh state on each navigation
        },
        replace: true  // Replace history to clear previous state
      });
    } catch (error) {
      console.error("Valuation error:", error);
      alert("Error getting valuation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (value) => {
    const field = steps[currentStep].field;
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);
    
    // Auto-advance after selection for button-type questions
    setTimeout(() => {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Pass the updated form data directly to avoid race condition
        handleSubmit(updatedFormData);
      }
    }, 300);
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  if (showForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-500 via-brand-500 to-brand-600 relative overflow-hidden">
        <Helmet>
          <title>AI Property Valuation - Get Instant Property Value | 99prop.com</title>
          <meta name="description" content="Get accurate AI-powered property valuation in Indirapuram, Vaishali, Vasundhara. Instant market value, quick sale price, and demand analysis. Zero brokerage on all deals." />
          <meta name="keywords" content="property valuation, AI valuation, real estate NCR, Indirapuram property, Vaishali property, Vasundhara property, zero brokerage" />
          
          {/* Open Graph Tags */}
          <meta property="og:title" content="AI Property Valuation - 99prop.com" />
          <meta property="og:description" content="Get instant, accurate property valuation powered by AI. Zero brokerage on all property deals." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://99prop.com" />
          <meta property="og:image" content="https://99prop.com/og-image.jpg" />
          
          {/* Twitter Card Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="AI Property Valuation - 99prop.com" />
          <meta name="twitter:description" content="Get instant property valuation powered by AI" />
          <meta name="twitter:image" content="https://99prop.com/og-image.jpg" />
        </Helmet>
        {/* Hero Background Image */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1627141234469-24711efb373c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlfGVufDB8fHx8MTc3MTc5NjA5Mnww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        ></div>

        {/* Header with Logo - Clickable to go back */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setShowForm(false);
                setCurrentStep(0);
                // Go back to previous page in history
                navigate(-1);
              }}
              className="flex items-center group cursor-pointer"
            >
              <h1 className="text-2xl sm:text-3xl font-bold text-white hover:text-brand-100 transition">
                99prop.com
              </h1>
            </button>
            
            <button
              onClick={() => {
                setShowForm(false);
                setCurrentStep(0);
                // Go back to previous page in history
                navigate(-1);
              }}
              className="text-white hover:text-brand-100 transition flex items-center space-x-2 text-sm sm:text-base"
            >
              <span>✕ Close</span>
            </button>
          </div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-4">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="bg-white bg-opacity-30 rounded-full h-3 overflow-hidden backdrop-blur-sm">
              <div 
                className="bg-white h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="text-white text-center mt-2 font-semibold">
              Step {currentStep + 1} of {steps.length}
            </p>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 min-h-[400px] flex flex-col justify-between" data-testid="valuation-form">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {steps[currentStep].question}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {steps[currentStep].subtitle}
              </p>

              {/* Input Fields Based on Type */}
              <div className="mb-8" style={{ position: 'relative', zIndex: 100 }}>
                {steps[currentStep].type === "select" && (
                  <div style={{ position: 'relative' }}>
                    {/* Custom Dropdown Display */}
                    <button
                      type="button"
                      onClick={() => setShowDropdown(!showDropdown)}
                      style={{
                        width: '100%',
                        padding: '20px',
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#000000',
                        backgroundColor: '#FFFFFF',
                        border: '4px solid #000000',
                        borderRadius: '12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: '70px'
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>
                        {formData[steps[currentStep].field] || "Select an option"}
                      </span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {showDropdown ? '▲' : '▼'}
                      </span>
                    </button>
                    
                    {/* Custom Dropdown Options */}
                    {showDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '8px',
                        backgroundColor: '#FFFFFF',
                        border: '4px solid #000000',
                        borderRadius: '12px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                      }}>
                        {steps[currentStep].options.map((option, index) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, [steps[currentStep].field]: option });
                              setShowDropdown(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '18px 20px',
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#000000',
                              backgroundColor: formData[steps[currentStep].field] === option ? '#E3F2FD' : '#FFFFFF',
                              border: 'none',
                              borderBottom: index < steps[currentStep].options.length - 1 ? '2px solid #E0E0E0' : 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F5F5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData[steps[currentStep].field] === option ? '#E3F2FD' : '#FFFFFF'}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(steps[currentStep].type === "text" || steps[currentStep].type === "number") && (
                  <input
                    type={steps[currentStep].type}
                    value={formData[steps[currentStep].field]}
                    onChange={(e) => setFormData({ ...formData, [steps[currentStep].field]: e.target.value })}
                    placeholder={steps[currentStep].placeholder}
                    className="w-full px-6 py-4 text-xl border-3 border-gray-300 rounded-2xl focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-blue-200 transition"
                    data-testid={`input-${steps[currentStep].field}`}
                  />
                )}

                {steps[currentStep].type === "buttons" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {steps[currentStep].options.map((opt) => {
                      const value = typeof opt === 'string' ? opt : opt.value;
                      const label = typeof opt === 'string' ? opt : opt.label;
                      const isSelected = formData[steps[currentStep].field] === value;
                      
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleOptionSelect(value)}
                          className={`px-6 py-6 rounded-2xl font-bold text-lg transition-all transform hover:scale-105 ${
                            isSelected
                              ? "bg-brand-500 text-white shadow-xl"
                              : "bg-gray-100 text-gray-800 hover:bg-brand-50 hover:shadow-lg"
                          }`}
                          data-testid={`option-${value}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-4">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 bg-gray-200 text-gray-700 py-4 px-6 rounded-xl font-bold text-lg hover:bg-gray-300 transition"
                  data-testid="back-button"
                >
                  ← Back
                </button>
              )}
              
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="flex-1 bg-brand-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-brand-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg"
                data-testid="next-button"
              >
                {loading ? (
                  "🔄 Analyzing..."
                ) : currentStep === steps.length - 1 ? (
                  "✨ Get My Valuation"
                ) : (
                  "Continue →"
                )}
              </button>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-8 text-white px-4">
            <div className="text-center min-w-[100px]">
              <div className="text-2xl sm:text-3xl font-bold">10,000+</div>
              <div className="text-xs sm:text-sm opacity-90">Properties Valued</div>
            </div>
            <div className="text-center min-w-[100px]">
              <div className="text-2xl sm:text-3xl font-bold">AI-Powered</div>
              <div className="text-xs sm:text-sm opacity-90">Accurate Results</div>
            </div>
            <div className="text-center min-w-[100px]">
              <div className="text-2xl sm:text-3xl font-bold">Instant</div>
              <div className="text-xs sm:text-sm opacity-90">Free Report</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Landing Page
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>99prop.com - Zero Brokerage Real Estate | AI Property Valuation in NCR</title>
        <meta name="description" content="Buy & Sell properties with zero brokerage in Indirapuram, Vaishali, Vasundhara. Get instant AI-powered property valuation. Trusted by 1000+ families in NCR." />
        <meta name="keywords" content="zero brokerage property, real estate NCR, Indirapuram property, Vaishali property, Vasundhara property, AI valuation, property valuation, buy property, sell property" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="99prop.com - Zero Brokerage Real Estate Platform" />
        <meta property="og:description" content="Buy & Sell properties with zero brokerage. AI-powered valuations. Trusted in NCR." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://99prop.com" />
        <meta property="og:image" content="https://99prop.com/og-image.jpg" />
        <meta property="og:site_name" content="99prop.com" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="99prop.com - Zero Brokerage Real Estate" />
        <meta name="twitter:description" content="Buy & Sell properties with zero brokerage in NCR" />
        <meta name="twitter:image" content="https://99prop.com/og-image.jpg" />
      </Helmet>
      {/* SECTION 1: Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-brand-500 via-brand-500 to-brand-600 relative overflow-hidden flex items-center">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/11729105/pexels-photo-11729105.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        ></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
          <div className="mb-6 sm:mb-8 animate-fade-in">
            <img 
              src="https://images.pexels.com/photos/9345636/pexels-photo-9345636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="North Indian Family with Child"
              className="w-32 h-32 sm:w-48 sm:h-48 lg:w-56 lg:h-56 mx-auto rounded-full shadow-2xl border-4 sm:border-8 border-white object-cover animate-pulse-subtle"
            />
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 drop-shadow-lg animate-fade-in px-2" data-testid="hero-headline">
            Check Your Property's Real Market Value Instantly
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-semibold drop-shadow-md mb-8 sm:mb-10 animate-fade-in px-2">
            AI-Based Valuation for Ghaziabad, Indirapuram, Vaishali, Vasundhara & Noida
          </p>

          <button
            onClick={() => setShowForm(true)}
            className="bg-white text-brand-500 px-6 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-full font-bold text-base sm:text-xl lg:text-2xl hover:bg-brand-50 transition shadow-2xl hover:scale-105 transform animate-fade-in"
            data-testid="start-valuation-btn"
          >
            🚀 Get Free Valuation Now
          </button>

          <div className="mt-10 sm:mt-16 flex flex-wrap justify-center gap-6 sm:gap-8 lg:gap-12 text-white animate-fade-in">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold">10,000+</div>
              <div className="text-sm sm:text-base lg:text-lg opacity-90">Properties Valued</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold">AI-Powered</div>
              <div className="text-sm sm:text-base lg:text-lg opacity-90">Accurate Results</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold">60 Sec</div>
              <div className="text-sm sm:text-base lg:text-lg opacity-90">Instant Report</div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: Sell Fast */}
      <div className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="animate-fade-in order-2 md:order-1 flex justify-center items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/xv3a6pp7_Sell%20your%20property%2099prop.com.png"
                alt="Real Estate Agent Handing Keys to Property Owner"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl object-cover h-64 sm:h-80 lg:h-96 mx-auto"
              />
            </div>
            
            <div className="animate-fade-in order-1 md:order-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Sell Your Property in 48 Hours.
              </h2>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-brand-500 mb-3 sm:mb-4">
                Know Its True Market Value in 60 Seconds.
              </h3>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8">
                AI-Powered Property Valuation & Fast-Track Sale Service for Ghaziabad, Indirapuram, Vaishali, Vasundhara & Noida.
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-brand-500 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg lg:text-xl hover:bg-brand-600 transition shadow-lg hover:scale-105 transform"
              >
                Check Value Now →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: Why Choose Us */}
      <div className="bg-gradient-to-br from-brand-50 to-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-3 sm:mb-4 animate-fade-in">
            Why Thousands of Owners Are Checking Their Property Value Today on 99prop.com
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 text-center mb-10 sm:mb-12 lg:mb-16 animate-fade-in px-4">
            Make informed decisions with AI-powered insights
          </p>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 animate-fade-in">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">📊</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Know the Real Market Price
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    Not broker guesswork – AI-powered accurate valuations based on real market data
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 animate-fade-in">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">📉</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Avoid Under/Over Pricing
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    Get the perfect price point to sell faster without leaving money on the table
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 animate-fade-in">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">⚡</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Verified Buyer Interest in 48 Hours
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    Connect with serious buyers ready to close deals quickly
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 animate-fade-in">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">💰</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Sell with Zero Brokerage
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    Close directly with zero commissions and complete transparency
                  </p>
                </div>
              </div>
            </div>

            <div className="sm:col-span-2 flex justify-center">
              <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 animate-fade-in max-w-2xl w-full">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">🏦</div>
                  <div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                      Understand Investor Demand
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                      Know exactly how attractive your property is in your locality
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sm:col-span-2 bg-gradient-to-r from-brand-500 to-brand-600 p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl text-white animate-fade-in">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl lg:text-5xl flex-shrink-0">🔒</div>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 sm:mb-3">
                    100% Confidential – No Public Listing Unless You Approve
                  </h3>
                  <p className="text-brand-100 text-sm sm:text-base lg:text-lg">
                    Your privacy is our priority. We never list your property publicly without your explicit permission
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_code-editor-40/artifacts/5fdvn7n8_Indian%20Home%20-%2099prop.com.png"
              alt="Modern North Indian Family Krishna Pooja at Home"
              className="rounded-2xl sm:rounded-3xl shadow-2xl mx-auto w-full max-w-3xl h-56 sm:h-72 lg:h-80 object-cover"
            />
          </div>
        </div>
      </div>

      {/* SECTION 4: Process */}
      <div className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-3 sm:mb-4 animate-fade-in">
            From Valuation to Closure – Simplified
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 text-center mb-10 sm:mb-12 lg:mb-16 animate-fade-in px-4">
            Your property sale journey in 4 simple steps
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="text-center animate-fade-in">
              <div className="bg-brand-500 text-white w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold mx-auto mb-4 sm:mb-6 shadow-lg">
                1
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg h-full">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  Enter Property Details
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Quick 60-second form with basic property information
                </p>
              </div>
            </div>

            <div className="text-center animate-fade-in">
              <div className="bg-brand-500 text-white w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold mx-auto mb-4 sm:mb-6 shadow-lg">
                2
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg h-full">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  Get AI Market Valuation
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Instant AI-powered pricing with market insights
                </p>
              </div>
            </div>

            <div className="text-center animate-fade-in">
              <div className="bg-brand-500 text-white w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold mx-auto mb-4 sm:mb-6 shadow-lg">
                3
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg h-full">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  Property Inspection
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Our team inspects and shares the best deal for you
                </p>
              </div>
            </div>

            <div className="text-center animate-fade-in">
              <div className="bg-brand-500 text-white w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold mx-auto mb-4 sm:mb-6 shadow-lg">
                4
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg h-full">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  Close the Deal
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Complete sale in 48 hours with verified buyers*
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <img 
              src="https://images.unsplash.com/photo-1745301558339-44eb3217d5da"
              alt="Modern Indian Home Living Area"
              className="rounded-2xl sm:rounded-3xl shadow-2xl mx-auto w-full max-w-3xl h-56 sm:h-72 lg:h-80 object-cover mb-6 sm:mb-8"
            />
            
            <button
              onClick={() => setShowForm(true)}
              className="bg-brand-500 text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-full font-bold text-lg sm:text-xl lg:text-2xl hover:bg-brand-600 transition shadow-2xl hover:scale-105 transform"
            >
              Start Your Valuation Now →
            </button>

            <p className="text-xs sm:text-sm text-gray-500 mt-4 sm:mt-6 px-4">
              *Conditions apply. Actual closure time may vary based on documentation and buyer availability.
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-12 sm:py-16 lg:py-20 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 animate-fade-in">
            Ready to Know Your Property's True Worth?
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl mb-8 sm:mb-10 animate-fade-in px-4">
            Join thousands of smart property owners who discovered their property's real value
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-white text-brand-500 px-8 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-full font-bold text-lg sm:text-xl lg:text-2xl hover:bg-brand-50 transition shadow-2xl hover:scale-105 transform animate-fade-in"
          >
            Get Free AI Valuation in 60 Seconds
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
